import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useRegister } from "../api";
import { Link } from "react-router-dom";

// Zod validation schema
const signUpSchema = z
  .object({
    name: z
      .string()
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be less than 50 characters"),
    email: z.string().email("Please enter a valid email address"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
    acceptTerms: z.boolean().refine((val) => val === true, {
      message: "You must accept the terms and conditions",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type SignUpFormData = z.infer<typeof signUpSchema>;

const SignUp: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    mode: "onChange",
  });

  const { mutateAsync, isSuccess, isPending, error } = useRegister();

  const onSubmit = async (data: SignUpFormData) => {
    const { email, password, name } = data;
    try {
      await mutateAsync({
        email,
        password,
        name,
      });
      reset(); // only if no error
    } catch (error: any) {
      console.error("Registration failed:", error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign Up - Coralytics</title>
        <meta
          name="description"
          content="Create your Coralytics account and start transforming your real estate business with AI-powered tools."
        />
      </Helmet>

      {/* Background with white */}
      <section
        className="relative min-h-screen flex items-center justify-center py-12"
        style={{
          background: "white",
        }}
      >
        <div className="container relative z-10">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8 col-sm-10">
              <div
                className="card shadow-lg border-0"
                style={{
                  borderRadius: "20px",
                  boxShadow:
                    "0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05)",
                }}
              >
                <div className="card-body p-5">
                  {/* Header */}
                  <div className="text-center mb-4">
                    <a className="navbar-brand logo-image" href="/home">
                      <img
                        src="/assets/images/Coralytics trans.webp"
                        alt="alternative"
                        style={{ maxHeight: "60px", margin: "0 auto" }}
                      />
                    </a>
                    <hr
                      className="mt-2 mb-3"
                      style={{
                        borderColor: "rgb(87, 60, 255)",
                        borderWidth: "2px",
                      }}
                    />
                  </div>

                  {/* Success Message */}
                  {isSuccess && (
                    <div
                      className="alert alert-success d-flex align-items-center mb-4"
                      role="alert"
                    >
                      <i className="fas fa-check-circle me-2"></i>
                      <div>
                        Account created successfully! Please check your email to
                        verify your account.
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {error && (
                    <div
                      className="alert alert-danger d-flex align-items-center mb-4"
                      role="alert"
                    >
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <div>Email already in use</div>
                    </div>
                  )}

                  {/* Sign Up Form */}
                  <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    {/* Name Field */}
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label fw-semibold">
                        Full Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control form-control-lg ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        id="name"
                        placeholder="Enter your full name"
                        style={{ borderColor: "#e2e8f0", padding: "14px 16px" }}
                        {...register("name")}
                      />
                      {errors.name && (
                        <div className="invalid-feedback d-block">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {errors.name.message}
                        </div>
                      )}
                    </div>

                    {/* Email Field */}
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label fw-semibold">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-control form-control-lg ${
                          errors.email ? "is-invalid" : ""
                        }`}
                        id="email"
                        placeholder="Enter your email address"
                        style={{ borderColor: "#e2e8f0", padding: "14px 16px" }}
                        {...register("email")}
                      />
                      {errors.email && (
                        <div className="invalid-feedback d-block">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {errors.email.message}
                        </div>
                      )}
                    </div>

                    {/* Password Field */}
                    <div className="mb-3">
                      <label
                        htmlFor="password"
                        className="form-label fw-semibold"
                      >
                        Create Password <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className={`form-control form-control-lg ${
                            errors.password ? "is-invalid" : ""
                          }`}
                          id="password"
                          placeholder="Create a strong password"
                          style={{
                            borderColor: "#e2e8f0",
                            padding: "14px 16px",
                            paddingRight: "50px",
                          }}
                          {...register("password")}
                        />
                        <button
                          type="button"
                          className="btn position-absolute"
                          style={{
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            color: "#6c757d",
                            padding: "0",
                            fontSize: "16px",
                          }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i
                            className={`fas ${
                              showPassword ? "fa-eye-slash" : "fa-eye"
                            }`}
                          ></i>
                        </button>
                      </div>
                      {errors.password && (
                        <div className="invalid-feedback d-block">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {errors.password.message}
                        </div>
                      )}
                    </div>

                    {/* Confirm Password Field */}
                    <div className="mb-3">
                      <label
                        htmlFor="confirmPassword"
                        className="form-label fw-semibold"
                      >
                        Confirm Password <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className={`form-control form-control-lg ${
                            errors.confirmPassword ? "is-invalid" : ""
                          }`}
                          id="confirmPassword"
                          placeholder="Confirm your password"
                          style={{
                            borderColor: "#e2e8f0",
                            padding: "14px 16px",
                            paddingRight: "50px",
                          }}
                          {...register("confirmPassword")}
                        />
                        <button
                          type="button"
                          className="btn position-absolute"
                          style={{
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            color: "#6c757d",
                            padding: "0",
                            fontSize: "16px",
                          }}
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          <i
                            className={`fas ${
                              showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                            }`}
                          ></i>
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <div className="invalid-feedback d-block">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {errors.confirmPassword.message}
                        </div>
                      )}
                    </div>

                    {/* Terms and Conditions Checkbox */}
                    <div className="mb-4">
                      <div className="form-check">
                        <input
                          className={`form-check-input ${
                            errors.acceptTerms ? "is-invalid" : ""
                          }`}
                          type="checkbox"
                          id="acceptTerms"
                          style={{
                            width: "18px",
                            height: "18px",
                            marginTop: "2px",
                          }}
                          {...register("acceptTerms")}
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="acceptTerms"
                        >
                          I agree to the{" "}
                          <a
                            href="/Terms-and-Conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "rgb(87, 60, 255)",
                              textDecoration: "none",
                              fontWeight: "600",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecoration =
                                "underline";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecoration = "none";
                            }}
                          >
                            Terms and Conditions
                          </a>{" "}
                          <span className="text-danger">*</span>
                        </label>
                      </div>
                      {errors.acceptTerms && (
                        <div className="invalid-feedback d-block mt-1">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {errors.acceptTerms.message}
                        </div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <div className="d-grid mb-4">
                      <button
                        type="submit"
                        className="btn btn-lg fw-semibold"
                        disabled={isPending}
                        style={{
                          background: "rgb(87, 60, 255)",
                          border: "none",
                          color: "white",
                          borderRadius: "12px",
                          padding: "12px 24px",
                          fontSize: "16px",
                          transition: "all 0.3s ease",
                          transform: isPending ? "scale(0.98)" : "scale(1)",
                          boxShadow: "0 4px 15px rgba(87, 60, 255, 0.3)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.02)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(87, 60, 255, 0.4)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 15px rgba(87, 60, 255, 0.3)";
                        }}
                      >
                        {isPending ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Creating Account...
                          </>
                        ) : (
                          <>Verify</>
                        )}
                      </button>
                    </div>

                    {/* Login Link */}
                    <div className="text-center">
                      <p className="mb-0" style={{ color: "#6c757d" }}>
                        Already have an account?{" "}
                        <Link
                          to="/login"
                          style={{
                            color: "rgb(87, 60, 255)",
                            textDecoration: "none",
                            fontWeight: "600",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = "underline";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = "none";
                          }}
                        >
                          Sign in here
                        </Link>
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SignUp;
